// Round 36 — Workflow Export/Import.
//
// Two small buttons in the CanvasWorkflowPanel header:
//   - Export: serialises {schemaVersion, exportedAt, items, triggers, variables, scoringMode}
//             into a JSON blob the browser downloads as aidea-workflow-<sceneId>.json.
//             exportedAt is passed in from the click handler (NOT computed in a pure
//             helper) so the rest of this module stays deterministic for tests.
//   - Import: opens a file picker for .json; on read parses + validates the schema +
//             dispatches the existing applyTemplateThunk to materialise items/triggers.
//
// Reuses applyTemplateThunk so import + template-application share a single
// machinery (id remapping, source tagging, etc.). The import treats the JSON
// shape as if it were a user template — same idea, different entry point.
import React, { useRef } from "react";
import { connect } from "react-redux";
import i18n from "i18next";
import { LuDownload, LuUpload } from "react-icons/lu";
import { applyTemplateThunk } from "../../../common/actions";
import { isFileTooLarge } from "../../../common/upload_limits.es6";

const SCHEMA_VERSION = 1;

// Pure builder for the export payload. Pure so it's trivially testable.
export function buildExportPayload({
  sceneId,
  items,
  triggers,
  variables,
  scoringMode,
  exportedAt,
}) {
  return {
    schemaVersion: SCHEMA_VERSION,
    exportedAt: exportedAt || null,
    sceneId: sceneId || null,
    items: Array.isArray(items) ? items : [],
    triggers: Array.isArray(triggers) ? triggers : [],
    variables: Array.isArray(variables) ? variables : [],
    scoringMode: scoringMode || "none",
  };
}

function downloadJsonBlob(payload, filename) {
  try {
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  } catch (e) {
    // Block: tests + headless environments without Blob support fall through.
    return false;
  }
}

function WorkflowExportImport({
  sectionId,
  sceneId,
  boxId,
  items,
  triggers,
  variables,
  scoringMode,
  onApplyTemplate,
}) {
  const fileInputRef = useRef(null);

  const onExportClick = () => {
    if (!sceneId) return;
    // Timestamp computed at click time (browser-only), not inside the pure
    // payload builder. Keeps tests deterministic.
    const ts =
      typeof window !== "undefined" && window.Date ? new window.Date().toISOString() : null;
    const payload = buildExportPayload({
      sceneId,
      items,
      triggers,
      variables,
      scoringMode,
      exportedAt: ts,
    });
    const filename = `aidea-workflow-${sceneId}.json`;
    downloadJsonBlob(payload, filename);
  };

  const onImportClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const onFileChange = (e) => {
    const file = e.target && e.target.files && e.target.files[0];
    if (!file) return;
    // 500 MB üst sınırı: aşan dosyayı işleme — sessiz reddet (bu akış MVP'de
    // hatalı JSON'ı da sessizce reddediyor), input'u sıfırla.
    if (isFileTooLarge(file)) {
      if (e.target) e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const raw = String(evt.target && evt.target.result ? evt.target.result : "");
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.schemaVersion !== SCHEMA_VERSION) return;
        // Reuse applyTemplateThunk — treat the imported JSON as if it were a
        // user template. Item ids will be remapped to fresh runtime ids by
        // the thunk so importing into a scene that already holds items
        // doesn't collide.
        if (onApplyTemplate) {
          onApplyTemplate(sectionId, sceneId, boxId, {
            id: `import-${file.name}`,
            title: file.name,
            items: parsed.items || [],
            triggers: parsed.triggers || [],
          });
        }
      } catch (_err) {
        // Block: malformed JSON or schema mismatch — fail silently for MVP.
      }
      // Reset the input so importing the same file twice fires onchange.
      if (e.target) e.target.value = "";
    };
    reader.readAsText(file);
  };

  return (
    <div className="workflow-export-import" data-testid="workflow-export-import">
      <button
        type="button"
        className="workflow-export-import__btn"
        onClick={onExportClick}
        title={i18n.t("Workflow.export_title", { defaultValue: "Export workflow as JSON" })}
        data-testid="workflow-export-btn"
      >
        <LuDownload size={16} />
      </button>
      <button
        type="button"
        className="workflow-export-import__btn"
        onClick={onImportClick}
        title={i18n.t("Workflow.import_title", { defaultValue: "Import workflow from JSON" })}
        data-testid="workflow-import-btn"
      >
        <LuUpload size={16} />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        onChange={onFileChange}
        style={{ display: "none" }}
        data-testid="workflow-import-input"
      />
    </div>
  );
}

// Picks the section workflow off the same slice CanvasWorkflowPanel reads
// for triggers — keeps the export view symmetric with what the user sees.
const mapStateToProps = (state) => {
  const present = (state.undoGroup && state.undoGroup.present) || {};
  const navItemsById = present.navItemsById || {};
  const navItemSelected = present.navItemSelected;
  const scene = navItemSelected ? navItemsById[navItemSelected] : null;
  // sectionId resolution mirrors CanvasWorkflowPanel: selected nav item that
  // IS a section uses itself, else .parent. Numeric 0 (course root) is a
  // valid section id — guard with explicit null/undefined check.
  const isSection = !!(scene && scene.type === "section");
  const candidateParent = scene && scene.parent;
  const sectionId = isSection
    ? navItemSelected
    : candidateParent !== null && candidateParent !== undefined && candidateParent !== ""
      ? candidateParent
      : null;
  const section = sectionId !== null && sectionId !== undefined ? navItemsById[sectionId] : null;
  const workflow = (section && section.workflow) || {};
  return {
    variables: workflow.variables || [],
    scoringMode: workflow.scoringMode || "none",
  };
};

const mapDispatchToProps = (dispatch) => ({
  onApplyTemplate: (sectionId, sceneId, boxId, template) =>
    dispatch(applyTemplateThunk(sectionId, sceneId, boxId, template)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkflowExportImport);
